import React, {useEffect, useRef, useState} from 'react';
import {useLoaderData, useParams, useSearchParams} from "react-router";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {Template} from "../../../../../components/template/template.jsx";
import {useQuery} from "@tanstack/react-query";
import corporationService from "../../../../../services/web/corporation-service.js";
import ecapTemplateService from "../../../../../services/web/ecap-template-service.js";
import ecapService from "../../../../../services/web/ecap-service.js";
import projectUtils from "../../../../../utils/project-utils.js";
import "../../../../../css/style-rich-text.css"
import EcapWysiwyg from "../../components/ecap-wysiwyg.jsx";
import Button from "../../../../../components/button/button.jsx";
import EcapHistoryModal from "./components/ecap-history-modal.jsx";
import Swal from "sweetalert2";
import {toast} from "react-toastify";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const EcapForm = () => {
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const navigate = navigateHook();

    const loaderData = useLoaderData();

    const [isLoading, setIsLoading] = useState(false);

    const [content, setContent] = useState("");
    const [headerTemplateSelected, setHeaderTemplateSelected] = useState(-1);
    const [ecapHistory, setEcapHistory] = useState([]);
    const [historyModal, setHistoryModal] = useState(false);
    const [solicitationId, setSolicitationId] = useState(null);
    const [templateId, setTemplateId] = useState(null);

    const editorRef = useRef(undefined);

    const {data: headerTemplate} = useQuery({
        queryFn: () => corporationService.headerTemplates(),
        queryKey: ["headerTemplates"]
    });

    const {data: template, isSuccess: isSuccessTemplate} = useQuery({
        queryFn: () => ecapTemplateService.get(searchParams.get("templateId")),
        queryKey: ["template"],
        enabled: !!templateId
    });

    const {data, isSuccess, refetch} = useQuery({
        queryFn: () => ecapService.get(id),
        queryKey: ["ecap"],
        enabled: !!id
    });

    useEffect(() => {
        if (!isSuccess) return;

        const {ecap, history} = data

        setContent(ecap.content);
        setHeaderTemplateSelected(ecap.headerTemplateId)
        setEcapHistory(history);
        setSolicitationId(ecap.solicitationId)
    }, [data, isSuccess]);

    useEffect(() => {
        if (!isSuccessTemplate) return;

        const {content, headerTemplateId} = template

        setContent(content);
        setHeaderTemplateSelected(headerTemplateId)
    }, [template, isSuccessTemplate]);

    useEffect(() => {
        if (searchParams.has('solicitationId')) setSolicitationId(searchParams.get('solicitationId'))

        if (searchParams.has('templateId')) setTemplateId(searchParams.get('templateId'))
    }, []);

    const apply = (genPDF) => {
        setIsLoading(true);

        const html = editorRef.current.getHTML();

        const formData = new FormData();
        formData.append("solicitationId", solicitationId)
        formData.append("headerTemplateId", headerTemplateSelected)
        formData.append("genPDF", genPDF)

        const formBlob = new Blob([html], {
            type: 'application/octet-stream'
        });

        formData.append("content", formBlob);

        let response;
        if (id) {
            formData.append("ecapId", id.replace("-","/"));
            response = ecapService.update(formData)
        }
        else response = ecapService.create(formData)

        response
            .then((data) => {
                if (data.gedId) {
                    window.open(`${API_KEEPER}/ged/attach/print/pdf/${data.gedId}`, data.gedId);
                }

                if (id) {
                    refetch();
                    toast.success("Salvo com sucesso");
                    return;
                }

                toast.success("Salvo com sucesso");
                navigate(`/ecap/form/${data.id.replace("/", "-")}`)
            })
            .catch(async error => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = translateError(error.response.data.message, "Não foi possível salvar.");
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    const deleteEcap = () => {
        Swal.fire({
            title: "Excluir",
            html: `
                    Excluir ${loaderData?.ecap_acronym} ${id?.replace("-", "/")}?<br>
                    Essa alteração não pode ser desfeita.
                `,
            icon: "warning",
            confirmButtonText: "Sim",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Não",
            cancelButtonColor: "#c10007",
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (!isConfirmed) return;

            setTimeout(() => {
                setIsLoading(true);

                ecapService.delete(id)
                    .then(() => {
                        toast.success("Excluído com sucesso");
                        navigate("/ecap");
                    })
                    .catch(async error => {
                        console.error(error);
                        let message = 'Não foi possível excluir.';
                        if (error.response.status !== 500) {
                            message = translateError(error.response.data.message, "Não foi possível excluir.");
                        }

                        projectUtils.swalErrorMessage('', message, 'Ok');
                    })
                    .finally(() => setIsLoading(false));
            }, 100);
        });
    }

    const translateError = (message, defaultMessage) => {
        const i18n = {
            "not.associate": "Não existe uma solicitação associada a esse ECAP",
            "empty.content": "Não é possível salvar um documento em branco.",
            "ecap.solicitaion.finalized": "Não foi possível salvar/excluir. A solicitação já foi finalizada.",
        }

        return i18n[message] ?? defaultMessage
    }

    const updateHeader = (headerId) => {
        if (editorRef.current.can().undo() || editorRef.current.can().redo()) {
            projectUtils.swalMessage(
                'warning',
                'Atenção',
                'Ao confirmar, o histórico de alterações será perdido. Deseja continuar?',
                'Sim',
                true,
                'Não',
                ({isConfirmed}) => {
                    if (!isConfirmed) return;

                    setHeaderTemplateSelected(headerId)
                    setContent(editorRef.current.getHTML());
                    toast.success("Salvo com sucesso");
                }
            )
        } else {
            setHeaderTemplateSelected(headerId)
            setContent(editorRef.current.getHTML());
        }
    }

    const openSolicitation = () => {
        navigate(`/ecap-solicitation/form/${solicitationId}`, true);
    }

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title browserTitle={`${loaderData?.ecap_acronym} ${id ? id.replace("-","/") : ""}`} className={"flex items-center gap-2"}>
                        {loaderData?.ecap_acronym} {id ? id.replace("-","/") : ""} -
                        {
                            solicitationId &&
                            <Button target={"default"} className="ecap-link text-[13px]" onClick={openSolicitation}>
                                Ver solicitação
                            </Button>
                        }
                    </Template.Title>

                    {
                        data &&
                        <div className={"flex flex-col items-end justify-center"} style={{height: "30px"}}>
                            <span>Salvo em {data.ecap.updatedAt ? projectUtils.formatDate(data.ecap.updatedAt) : projectUtils.formatDate(data.ecap.createdAt)}</span>

                            {
                                ecapHistory.length ?
                                    <span>PDF gerado em {projectUtils.formatDate(ecapHistory.at(-1).created_at)}</span> :
                                    <span>PDF não gerado</span>
                            }
                        </div>
                    }
                </Template.Header>

                <Template.Body>
                    <EcapWysiwyg
                        id={id?.replace("-", "/")}
                        content={content}
                        editorRef={editorRef}
                        apply={() => apply(false)}
                        updateAndGenPdf={() => apply(true)}
                        deleteEcap={deleteEcap}
                        updateHeader={updateHeader}
                        headerTemplate={headerTemplate}
                        headerTemplateSelected={headerTemplate?.find(item => item.id === headerTemplateSelected)}
                        data={data}
                        ecapName={loaderData?.ecap_name}
                        setHistoryModal={setHistoryModal}
                        showHistory={ecapHistory.length > 0}
                        setIsLoading={setIsLoading}
                        enableSaveActionFeedback
                        enableDeleteActionFeedback
                    />
                </Template.Body>
            </Template>

            <EcapHistoryModal
                open={historyModal}
                setOpen={setHistoryModal}
                history={ecapHistory}
            />
        </>
    );
};

export default EcapForm;